/**
 * Date and time utilities for consistent formatting across the application
 */

/**
 * Parse a date string and return formatted date and time
 * @param {string} dateStr - YYYY-MM-DD date string or ISO datetime string
 * @param {string} timeStr - Optional time string (HH:MM)
 * @returns {Object} - Object with formatted date and time
 */
export const parseDateTime = (dateStr, timeStr = null) => {
  if (!dateStr) return { date: null, time: null, datetime: null };
  
  try {
    // Extract the date portion without timezone conversion
    // This preserves the calendar date regardless of user's timezone
    let year, month, day;
    let extractedTime = timeStr;
    
    if (dateStr.includes('T')) {
      // ISO datetime string - extract just the date and optionally time
      const datePart = dateStr.split('T')[0];
      [year, month, day] = datePart.split('-').map(Number);
      
      // Extract time from ISO string if no separate time provided
      if (!timeStr) {
        const timePart = dateStr.split('T')[1];
        if (timePart) {
          extractedTime = timePart.substring(0, 5); // HH:MM
        }
      }
    } else if (dateStr.includes('-')) {
      // Simple YYYY-MM-DD format
      [year, month, day] = dateStr.split('-').map(Number);
    } else {
      // Fallback for unexpected formats
      console.warn('Unexpected date format in parseDateTime:', dateStr);
      return { date: dateStr, time: timeStr, datetime: null };
    }
    
    // Create the date as LOCAL time to preserve calendar date
    const dt = new Date(year, month - 1, day);
    
    if (extractedTime) {
      const [hours, minutes] = extractedTime.split(':').map(Number);
      dt.setHours(hours, minutes, 0, 0);
    }
    
    // Format the date string in YYYY-MM-DD format
    const formattedDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    return {
      date: formattedDateStr,
      time: extractedTime || '00:00',
      datetime: dt,
      formattedDate: formatDate(formattedDateStr),
      formattedTime: extractedTime ? formatMilitaryTime(extractedTime) : '00:00'
    };
  } catch (error) {
    console.error('Error parsing datetime:', error);
    return { date: dateStr, time: timeStr, datetime: null };
  }
};

/**
 * Format a date object or string to display format
 * @param {Date|string} date - Date to format
 * @returns {string} - Formatted date string (MM/DD/YYYY)
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  let dt;
  if (typeof date === 'string') {
    // Handle ISO date strings properly to avoid timezone shifts
    if (date.includes('T')) {
      // Extract just the date part to avoid timezone conversion issues
      const datePart = date.split('T')[0];
      const [year, month, day] = datePart.split('-');
      dt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      // Simple date string like "2024-01-15"
      const [year, month, day] = date.split('-');
      dt = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  } else {
    dt = date;
  }
  
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  const year = dt.getFullYear();
  
  return `${month}/${day}/${year}`;
};

/**
 * Format time to military format (24-hour)
 * @param {Date|string} time - Time to format (Date object or HH:MM string)
 * @returns {string} - Time in HH:MM format
 */
export const formatMilitaryTime = (time) => {
  if (!time) return '';
  
  if (time instanceof Date) {
    const hours = String(time.getHours()).padStart(2, '0');
    const minutes = String(time.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }
  
  // If it's already a string, ensure it's in HH:MM format
  if (typeof time === 'string') {
    const parts = time.split(':');
    if (parts.length >= 2) {
      const hours = String(parseInt(parts[0])).padStart(2, '0');
      const minutes = String(parseInt(parts[1])).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
  }
  
  return time;
};

/**
 * Check if a datetime has expired (is in the past)
 * @param {string} dateStr - Date string (YYYY-MM-DD or ISO format)
 * @param {string} timeStr - Time string (HH:MM)
 * @returns {boolean} - True if expired
 */
export const isDateTimeExpired = (dateStr, timeStr) => {
  if (!dateStr) return false;
  
  try {
    // Parse the date string as a LOCAL date to avoid timezone shifts
    // This is critical because dates like "2026-01-03" represent CALENDAR dates,
    // not moments in time. Using new Date("2026-01-03") parses as UTC midnight,
    // which shifts to the previous day in US timezones.
    
    let year, month, day;
    
    if (dateStr.includes('T')) {
      // ISO datetime string - extract just the date part
      const datePart = dateStr.split('T')[0];
      [year, month, day] = datePart.split('-').map(Number);
    } else if (dateStr.includes('-')) {
      // Simple YYYY-MM-DD format
      [year, month, day] = dateStr.split('-').map(Number);
    } else {
      // Unexpected format, try standard parsing as fallback
      console.warn('Unexpected date format:', dateStr);
      return new Date(dateStr) < new Date();
    }
    
    // Create date as LOCAL time (not UTC) to preserve the calendar date
    const date = new Date(year, month - 1, day);
    
    if (timeStr) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
    } else {
      // If no time specified, use end of day for expiration check
      date.setHours(23, 59, 59, 999);
    }
    
    return date < new Date();
  } catch (error) {
    console.error('Error checking if datetime expired:', error);
    return false;
  }
};

/**
 * Format datetime for display with both date and time
 * @param {string} dateStr - Date string
 * @param {string} timeStr - Time string
 * @returns {string} - Formatted datetime string
 */
export const formatDateTime = (dateStr, timeStr) => {
  if (!dateStr) return '';
  
  const parsed = parseDateTime(dateStr, timeStr);
  return `${parsed.formattedDate} ${parsed.formattedTime}`;
};

/**
 * Get relative time description (e.g., "2 hours ago", "in 3 days")
 * @param {string} dateStr - Date string
 * @param {string} timeStr - Optional time string
 * @returns {string} - Relative time description
 */
export const getRelativeTime = (dateStr, timeStr = null) => {
  if (!dateStr) return '';
  
  try {
    const parsed = parseDateTime(dateStr, timeStr);
    const now = new Date();
    const diff = parsed.datetime - now;
    const absDiff = Math.abs(diff);
    
    const minutes = Math.floor(absDiff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (diff < 0) {
      // Past
      if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
      if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      return 'just now';
    } else {
      // Future
      if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
      if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
      if (minutes > 0) return `in ${minutes} minute${minutes > 1 ? 's' : ''}`;
      return 'now';
    }
  } catch (error) {
    console.error('Error calculating relative time:', error);
    return '';
  }
};
